/* -------------------------------------------------------------------------- */
/*  Budget-import framework — helpers shared across tenants                     */
/* -------------------------------------------------------------------------- */

import { ValidationError } from "../../../errors";
import { BudgetRow } from "./types";

/**
 * Escape a value for a comma-delimited CSV field: wrap in quotes and double any embedded quotes
 * when the value contains a comma, quote or newline.
 */
export const csvEscape = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

/**
 * Format a list for a validation message: the first `max` items joined by ", ", with an
 * "and N more" suffix when the list is longer.
 */
export const truncateList = (items: string[], max = 5): string => {
  const shown = items.slice(0, max).join(", ");
  const remaining = items.length - max;
  return remaining > 0 ? `${shown} and ${remaining} more` : shown;
};

/** Strip leading zeros from a code (e.g. Sage phase codes stored with padding). */
export const stripLeadingZeros = (value: string): string => value.replace(/^0+/, "");

/**
 * Fail the export when the detail records it built do not add up to the rows they came from.
 * LMSEO proration can leave up to a cent of rounding per line item, so the tolerance scales with
 * the line-item count rather than being a fixed figure.
 */
export const assertDetailTotalMatches = (rows: BudgetRow[], detailTotal: number): void => {
  const lineItemCount = rows.filter((row) => row.type === "line_item").length;
  const tolerance = Math.max(0.01, lineItemCount * 0.01);
  const estimateTotal = rows.reduce((sum: number, row) => sum + (Number(row.total) || 0), 0);

  if (Math.abs(estimateTotal - detailTotal) > tolerance) {
    throw new ValidationError(
      `Cost detail total ($${detailTotal.toFixed(2)}) does not match estimate total ($${estimateTotal.toFixed(2)}). This may indicate a data issue — please verify line item costs before exporting.`
    );
  }
};
